import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Quill from "quill";
import "quill/dist/quill.core.css";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import StrikethroughSIcon from "@material-ui/icons/StrikethroughS";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import FormatColorTextIcon from "@material-ui/icons/FormatColorText";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import FormatClearIcon from "@material-ui/icons/FormatClear";
import FormatSizeIcon from "@material-ui/icons/FormatSize";
import FlareRoundedIcon from "@material-ui/icons/FlareRounded";
import CheckIcon from "@material-ui/icons/Check";
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";

import { sanitizarHtml } from "../../utils/regrasHtml";
import { VERDE } from "../../utils/estiloNavy";

// ── Formatos próprios do manual (viram classes "rg-...") ──
const Parchment = Quill.import("parchment");
const Brilho = new Parchment.Attributor.Class("brilho", "rg-brilho", {
  scope: Parchment.Scope.INLINE,
  whitelist: ["arcoiris", "verde", "ouro", "fogo"],
});
const Efeito = new Parchment.Attributor.Class("efeito", "rg-efeito", {
  scope: Parchment.Scope.INLINE,
  whitelist: ["alerta"],
});
const Tamanho = new Parchment.Attributor.Class("tam", "rg-tam", {
  scope: Parchment.Scope.INLINE,
  whitelist: ["grande"],
});
Quill.register(Brilho, true);
Quill.register(Efeito, true);
Quill.register(Tamanho, true);

const FORMATOS = ["bold", "italic", "underline", "strike", "color", "background", "list", "brilho", "efeito", "tam"];

export const CORES_TEXTO = [
  { nome: "Preto", cor: "#0f172a" },
  { nome: "Cinza", cor: "#64748b" },
  { nome: "Verde", cor: "#16a34a" },
  { nome: "Azul", cor: "#2563eb" },
  { nome: "Vermelho", cor: "#dc2626" },
  { nome: "Laranja", cor: "#ea580c" },
  { nome: "Amarelo", cor: "#ca8a04" },
  { nome: "Roxo", cor: "#9333ea" },
  { nome: "Rosa", cor: "#db2777" },
  { nome: "Branco", cor: "#ffffff" },
];

export const MARCA_TEXTO = [
  { nome: "Amarelo", cor: "#fef08a" },
  { nome: "Verde", cor: "#bbf7d0" },
  { nome: "Azul", cor: "#bfdbfe" },
  { nome: "Vermelho", cor: "#fecaca" },
  { nome: "Roxo", cor: "#e9d5ff" },
  { nome: "Laranja", cor: "#fed7aa" },
];

export const BRILHOS = [
  { nome: "Arco-íris", formato: "brilho", valor: "arcoiris", classe: "rg-brilho-arcoiris" },
  { nome: "Neon verde", formato: "brilho", valor: "verde", classe: "rg-brilho-verde" },
  { nome: "Dourado", formato: "brilho", valor: "ouro", classe: "rg-brilho-ouro" },
  { nome: "Fogo", formato: "brilho", valor: "fogo", classe: "rg-brilho-fogo" },
  { nome: "Alerta piscando", formato: "efeito", valor: "alerta", classe: "rg-efeito-alerta" },
];

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(255,255,255,.1)" : "#e2e8f0";
  return {
    caixa: {
      border: `1px solid ${borda}`,
      borderRadius: 12,
      background: escuro ? "rgba(255,255,255,.02)" : "#fff",
      transition: "box-shadow .2s, border-color .2s",
      "&:focus-within": { borderColor: VERDE, boxShadow: "0 0 0 4px rgba(30,152,85,.12)" },
    },
    barra: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 2,
      padding: 4,
      borderBottom: `1px solid ${borda}`,
      position: "relative",
    },
    btn: {
      height: 30,
      minWidth: 30,
      border: "none",
      borderRadius: 8,
      background: "transparent",
      color: theme.palette.text.secondary,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      cursor: "pointer",
      fontFamily: "inherit",
      position: "relative",
      "&:hover": { background: escuro ? "rgba(255,255,255,.08)" : "#f1f5f9", color: theme.palette.text.primary },
      "& svg": { fontSize: 18 },
    },
    btnAtivo: {
      background: escuro ? "rgba(30,152,85,.25) !important" : "#dcfce7 !important",
      color: `${escuro ? "#4ade80" : VERDE} !important`,
      boxShadow: `inset 0 0 0 1px ${escuro ? "rgba(74,222,128,.35)" : "rgba(30,152,85,.35)"}`,
    },
    btnTexto: { padding: "0 8px", fontSize: "0.75rem", fontWeight: 800 },
    faixaCor: { position: "absolute", left: 7, right: 7, bottom: 3, height: 3, borderRadius: 2 },
    separador: { width: 1, height: 20, background: borda, margin: "0 4px" },
    paleta: {
      position: "absolute",
      top: "100%",
      left: 4,
      zIndex: 20,
      marginTop: 4,
      padding: 10,
      borderRadius: 12,
      background: escuro ? "#1d2522" : "#fff",
      border: `1px solid ${borda}`,
      boxShadow: "0 12px 30px rgba(2,6,23,.25)",
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      maxWidth: 330,
    },
    paletaTitulo: {
      width: "100%",
      fontSize: "0.7rem",
      fontWeight: 800,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    bolinhaCor: {
      width: 26,
      height: 26,
      borderRadius: "50%",
      border: `2px solid ${escuro ? "#0f172a" : "#fff"}`,
      boxShadow: "0 0 0 1px rgba(0,0,0,.18)",
      cursor: "pointer",
      padding: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "transform .12s",
      "&:hover": { transform: "scale(1.12)" },
      "& svg": { fontSize: 15 },
    },
    bolinhaMarcada: { boxShadow: `0 0 0 2px ${VERDE}` },
    opcaoTexto: {
      border: `1px solid ${borda}`,
      borderRadius: 8,
      background: "transparent",
      color: theme.palette.text.primary,
      padding: "4px 10px",
      cursor: "pointer",
      fontFamily: "inherit",
      fontWeight: 800,
      fontSize: "0.82rem",
      "&:hover": { borderColor: VERDE },
    },
    opcaoMarcada: { borderColor: VERDE, boxShadow: "0 0 0 3px rgba(30,152,85,.15)" },
    dica: { fontSize: "0.72rem", opacity: 0.7, width: "100%" },
    editor: {
      "& .ql-container": { fontFamily: "inherit", fontSize: "0.95rem", border: "none" },
      "& .ql-editor": {
        minHeight: 80,
        padding: "10px 12px",
        lineHeight: 1.6,
        color: theme.palette.text.primary,
        caretColor: theme.palette.text.primary,
        "& *": { caretColor: theme.palette.text.primary },
        "& p": { margin: 0 },
      },
      "& .ql-editor.ql-blank::before": {
        color: theme.palette.text.disabled,
        fontStyle: "normal",
        left: 12,
        right: 12,
      },
    },
  };
});

/**
 * Editor de texto do manual (estilo Word): os botões acendem conforme o
 * trecho onde está o cursor, e o estilo escolhido continua valendo enquanto
 * digita. Feito com o Quill, que cuida do cursor e das seleções.
 */
const EditorTexto = ({ valor, onChange, placeholder = "Escreva aqui...", minimo }) => {
  const classes = useStyles();
  const caixaRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [paleta, setPaleta] = useState(null); // "cor" | "marca" | "brilho"
  const [ativos, setAtivos] = useState({});

  onChangeRef.current = onChange;

  useEffect(() => {
    const quill = new Quill(caixaRef.current, {
      formats: FORMATOS,
      placeholder,
      modules: { toolbar: false, history: { delay: 600, maxStack: 200, userOnly: true } },
    });
    quillRef.current = quill;

    const inicial = sanitizarHtml(valor || "");
    if (inicial) quill.clipboard.dangerouslyPasteHTML(inicial, "silent");

    quill.on("text-change", () => {
      const html = quill.getText().trim() ? quill.root.innerHTML : "";
      onChangeRef.current(html);
    });
    quill.on("editor-change", () => {
      const r = quill.getSelection();
      if (r) setAtivos(quill.getFormat(r));
    });
    return () => {
      quillRef.current = null;
    };
    // só na montagem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aplica (ou tira) um formato no trecho selecionado / no que vai digitar
  const formatar = (nome, valorFormato) => {
    const quill = quillRef.current;
    if (!quill) return;
    quill.getSelection(true); // garante o foco/cursor no texto
    quill.format(nome, valorFormato, "user");
    const r = quill.getSelection();
    if (r) setAtivos(quill.getFormat(r));
  };

  const alternar = (nome) => formatar(nome, !ativos[nome]);

  const escolherBrilho = (b) => {
    const ligado = ativos[b.formato] === b.valor;
    formatar("brilho", false);
    formatar("efeito", false);
    if (!ligado) formatar(b.formato, b.valor);
    setPaleta(null);
  };

  const lista = (tipo) => formatar("list", ativos.list === tipo ? false : tipo);

  const limpar = () => {
    const quill = quillRef.current;
    if (!quill) return;
    const r = quill.getSelection(true);
    if (r && r.length) quill.removeFormat(r.index, r.length, "user");
    else FORMATOS.forEach((f) => f !== "list" && quill.format(f, false, "user"));
    const r2 = quill.getSelection();
    if (r2) setAtivos(quill.getFormat(r2));
  };

  const desfazer = () => quillRef.current?.history.undo();
  const refazer = () => quillRef.current?.history.redo();

  // onMouseDown + preventDefault: o clique não tira o cursor do texto
  const botao = (titulo, acao, conteudo, ativo = false, extra = "") => (
    <button
      type="button"
      title={titulo}
      className={`${classes.btn} ${ativo ? classes.btnAtivo : ""} ${extra}`}
      onMouseDown={(e) => {
        e.preventDefault();
        acao();
      }}
    >
      {conteudo}
    </button>
  );

  const mesmaCor = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();

  const bolinha = (c, formato) => {
    const marcada = mesmaCor(ativos[formato], c.cor);
    return (
      <button
        key={c.cor}
        type="button"
        title={c.nome}
        className={`${classes.bolinhaCor} ${marcada ? classes.bolinhaMarcada : ""}`}
        style={{ background: c.cor }}
        onMouseDown={(e) => {
          e.preventDefault();
          formatar(formato, marcada ? false : c.cor);
          setPaleta(null);
        }}
      >
        {marcada && <CheckIcon style={{ color: ["#ffffff", "#fef08a", "#bbf7d0", "#bfdbfe", "#fecaca", "#e9d5ff", "#fed7aa"].includes(c.cor) ? "#0f172a" : "#fff" }} />}
      </button>
    );
  };

  const brilhoAtivo = BRILHOS.find((b) => ativos[b.formato] === b.valor);

  return (
    <div className={classes.caixa}>
      <div className={classes.barra}>
        {botao("Desfazer (Ctrl+Z)", desfazer, <UndoIcon />)}
        {botao("Refazer (Ctrl+Y)", refazer, <RedoIcon />)}
        <span className={classes.separador} />
        {botao("Negrito (Ctrl+B)", () => alternar("bold"), <FormatBoldIcon />, !!ativos.bold)}
        {botao("Itálico (Ctrl+I)", () => alternar("italic"), <FormatItalicIcon />, !!ativos.italic)}
        {botao("Sublinhado (Ctrl+U)", () => alternar("underline"), <FormatUnderlinedIcon />, !!ativos.underline)}
        {botao("Riscado", () => alternar("strike"), <StrikethroughSIcon />, !!ativos.strike)}
        <span className={classes.separador} />
        {botao(
          "Cor da letra",
          () => setPaleta((p) => (p === "cor" ? null : "cor")),
          <>
            <FormatColorTextIcon />
            <span className={classes.faixaCor} style={{ background: ativos.color || "currentColor" }} />
          </>,
          paleta === "cor" || !!ativos.color
        )}
        {botao(
          "Marca-texto",
          () => setPaleta((p) => (p === "marca" ? null : "marca")),
          <>
            <BorderColorIcon />
            {ativos.background && <span className={classes.faixaCor} style={{ background: ativos.background }} />}
          </>,
          paleta === "marca" || !!ativos.background
        )}
        {botao(
          "Letra brilhante",
          () => setPaleta((p) => (p === "brilho" ? null : "brilho")),
          <>
            <FlareRoundedIcon style={{ fontSize: 16, color: "#a855f7" }} />
            <span className="rg-brilho-arcoiris">Brilho</span>
          </>,
          paleta === "brilho" || !!brilhoAtivo,
          classes.btnTexto
        )}
        {botao("Letra grande", () => formatar("tam", ativos.tam ? false : "grande"), <FormatSizeIcon />, !!ativos.tam)}
        <span className={classes.separador} />
        {botao("Lista", () => lista("bullet"), <FormatListBulletedIcon />, ativos.list === "bullet")}
        {botao("Lista numerada", () => lista("ordered"), <FormatListNumberedIcon />, ativos.list === "ordered")}
        {botao("Limpar formatação", limpar, <FormatClearIcon />)}

        {paleta === "cor" && (
          <div className={classes.paleta}>
            <div className={classes.paletaTitulo}>Cor da letra</div>
            {CORES_TEXTO.map((c) => bolinha(c, "color"))}
            <button
              type="button"
              className={classes.opcaoTexto}
              onMouseDown={(e) => {
                e.preventDefault();
                formatar("color", false);
                setPaleta(null);
              }}
            >
              Cor padrão
            </button>
            <div className={classes.dica}>Selecione um trecho para pintar, ou escolha a cor e continue digitando.</div>
          </div>
        )}
        {paleta === "marca" && (
          <div className={classes.paleta}>
            <div className={classes.paletaTitulo}>Marca-texto</div>
            {MARCA_TEXTO.map((c) => bolinha(c, "background"))}
            <button
              type="button"
              className={classes.opcaoTexto}
              onMouseDown={(e) => {
                e.preventDefault();
                formatar("background", false);
                setPaleta(null);
              }}
            >
              Sem marca
            </button>
          </div>
        )}
        {paleta === "brilho" && (
          <div className={classes.paleta}>
            <div className={classes.paletaTitulo}>Letra brilhante</div>
            {BRILHOS.map((b) => (
              <button
                key={b.classe}
                type="button"
                className={`${classes.opcaoTexto} ${brilhoAtivo === b ? classes.opcaoMarcada : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  escolherBrilho(b);
                }}
              >
                <span className={b.classe}>{b.nome}</span>
              </button>
            ))}
            <div className={classes.dica}>Clique de novo no mesmo brilho para desligar.</div>
          </div>
        )}
      </div>
      <div className={classes.editor} style={minimo ? { minHeight: minimo } : undefined}>
        <div ref={caixaRef} />
      </div>
    </div>
  );
};

export default EditorTexto;
